import React from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import { Label } from '@atlaskit/form';
import Lozenge from '@atlaskit/lozenge';
import { ModalBody, ModalTitle, ModalHeader, ModalFooter } from '@atlaskit/modal-dialog';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { HelpCenterUrls } from '../../services/use-properties/types.tsx';
import messages from './messages.tsx';

export type Props = {
	readonly onClose: (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	helpCenterUrls: HelpCenterUrls;
};

export const UrlUpdateDialog = ({ onClose, helpCenterUrls }: Props) => {
	const { newUrl, oldUrl } = helpCenterUrls;
	const { formatMessage } = useIntl();
	return (
		<ModalDialog
			messageId="help-center-custom-domain-change-boarding.ui.url-update-dialog.modal-dialog"
			messageType="transactional"
			key="help-center-url-update"
			width="medium"
		>
			<ModalHeader>
				<ModalTitle>{formatMessage(messages.modalTitle)}</ModalTitle>
			</ModalHeader>

			<ModalBody>
				<LozengeWrapper>
					<LabelWrapper>
						<Label htmlFor="custom-domain-url">
							{formatMessage(messages.customDomainUrlLabel)}
						</Label>
					</LabelWrapper>
					<Lozenge appearance="new">{formatMessage(messages.newUrl)}</Lozenge>
				</LozengeWrapper>
				<TextFieldWrapper>
					<Textfield value={newUrl} isReadOnly appearance="standard" id="custom-domain-url" />
				</TextFieldWrapper>
				<LozengeWrapper>
					<Label htmlFor="default-url">{formatMessage(messages.defaultUrlLabel)}</Label>
				</LozengeWrapper>
				<TextFieldWrapper>
					<Textfield value={oldUrl} isReadOnly appearance="standard" id="default-url" />
				</TextFieldWrapper>
				<ModalDescription>{formatMessage(messages.modalDescription)}</ModalDescription>
				<a
					target="_blank"
					href="https://support.atlassian.com/organization-administration/docs/update-your-product-and-site-url"
				>
					{formatMessage(messages.linkLearnMoreCustomDomain)}
				</a>
			</ModalBody>
			<ModalFooter>
				<Button autoFocus appearance="primary" onClick={onClose}>
					{formatMessage(messages.acknowledgeUpdateButton)}
				</Button>
			</ModalFooter>
		</ModalDialog>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelWrapper = styled.span({
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	paddingBottom: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextFieldWrapper = styled.div({
	marginBottom: token('space.200'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'input[data-ds--text-field--input]': {
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.medium'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalDescription = styled.div({
	marginTop: 0,
	marginRight: 0,
	marginBottom: token('space.200'),
	marginLeft: 0,
});
